import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import IconWrapper from '../../IconWrapper';
import { FiArrowLeft } from 'react-icons/fi';
import { cn } from '../../../lib/utils';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  onClick?: () => void;
}

interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  quickActions?: React.ReactNode;
  onBack?: () => void;
  showBackButton?: boolean;
  className?: string;
}

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({
  items,
  quickActions,
  onBack,
  showBackButton = true,
  className,
}) => {
  const navigate = useNavigate();

  const handleItemClick = (item: BreadcrumbItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (items.length === 0) {
    return null;
  }

  const allItemsExceptLast = items.slice(0, -1);
  const lastItem = items[items.length - 1];

  return (
    <div
      className={cn(
        'w-full bg-white px-6 py-3',
        className
      )}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: Back button + Breadcrumb trail */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {showBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex-shrink-0 h-8 px-2 text-gray-600 hover:text-gray-900"
            >
              <IconWrapper icon={FiArrowLeft} className="h-4 w-4" />
            </Button>
          )}

          {/* Breadcrumb items */}
          <nav className="flex items-center gap-2 flex-1 min-w-0" aria-label="Breadcrumb">
            {allItemsExceptLast.map((item, index) => (
              <React.Fragment key={index}>
                <button
                  onClick={() => handleItemClick(item)}
                  className="text-sm text-gray-600 hover:text-gray-900 hover:underline truncate transition-colors"
                  title={item.label}
                >
                  {item.label}
                </button>
                <span className="text-gray-400 text-sm flex-shrink-0">/</span>
              </React.Fragment>
            ))}
            {/* Last item - current location (non-clickable) */}
            <span
              className="text-sm font-semibold text-gray-900 truncate"
              title={lastItem.label}
            >
              {lastItem.label}
            </span>
          </nav>
        </div>

        {/* Right: Quick actions */}
        {quickActions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {quickActions}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreadcrumbNav;

